import { LottiePlayer } from '@repo/design-system-kit/components/lottie-player/lottie-player';
import { Popup } from '@repo/design-system-kit/components/popup/popup';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import otpConfirmationAnimation from '../../../lotties/otp-confirmation-code.json';
import { CodeVerificationSvg } from '../../../svgs/code-verification';
import { VerificationCode } from '#components/inputs/verification-code';
import type { MfaMethod } from '../../../providers/user-provider';
import styles from './mfa.module.css';

interface ConfirmationProps {
  mfa: {
    method: MfaMethod;
    phoneNumber?: string;
    email?: string;
  };
  otpErrorMessage?: string;
  onClose?: () => void;
  onSendSMS: () => void;
  onConfirmOtp: ({ otpToken }: { otpToken: string }) => Promise<void>;
}

export function Confirmation({
  onClose,
  onSendSMS,
  onConfirmOtp,
  mfa,
  otpErrorMessage,
}: ConfirmationProps): React.ReactNode {
  const isOtpInvalid = otpErrorMessage !== undefined;
  // NOTE -- this is needed until we have a better way to handle the portal container in qunit tests
  // https://www.notion.so/qonto/React-popover-library-used-within-react-aria-components-does-mount-popovers-modals-outside-of-qunit--12631ee4c696806e87dbef81e9a27922
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;
  const [isOpen, setIsOpen] = useState(true);
  const [isPending, setIsPending] = useState(false);
  const { formatMessage } = useIntl();
  const description = useMemo(() => {
    // oxlint-disable-next-line typescript/switch-exhaustiveness-check
    switch (mfa.method) {
      case 'by_sms':
        return mfa.phoneNumber
          ? formatMessage({ id: 'otp.confirmation_subtitle' }, { phoneOrEmail: mfa.phoneNumber })
          : formatMessage({ id: 'otp.confirmation_subtitle_phone' });
      case 'by_email':
        return mfa.email
          ? formatMessage({ id: 'otp.confirmation_subtitle' }, { phoneOrEmail: mfa.email })
          : formatMessage({ id: 'otp.confirmation_subtitle_email' });
      default:
        return formatMessage({ id: 'otp.confirmation_app_subtitle' });
    }
  }, [formatMessage, mfa.email, mfa.method, mfa.phoneNumber]);

  const handleClose = (): void => {
    setIsOpen(false);
    onClose?.();
  };

  const handleComplete = async (otpValue: string): Promise<void> => {
    if (isPending) return;
    setIsPending(true);
    try {
      await onConfirmOtp({ otpToken: otpValue });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Popup.Root
      aria-label={formatMessage({ id: 'otp.confirmation_title' })}
      data-testid="mfa-confirmation"
      id="mfa-confirmation"
      isOpen={isOpen}
      portalContainer={TEST_PORTAL_CONTAINER}
      role="dialog"
    >
      <Popup.CloseButton onPress={handleClose} />
      <Popup.Header>
        <figure className={styles.illustrationWrap}>
          {mfa.method === 'by_email' ? (
            <CodeVerificationSvg className={styles.illustration} data-img="email" role="img" />
          ) : (
            <LottiePlayer
              animationData={otpConfirmationAnimation}
              className="mb-24"
              data-img="sms-animation"
              loop
              role="img"
            />
          )}
        </figure>
        <Popup.Title>
          <FormattedMessage id="otp.confirmation_title" />
        </Popup.Title>
        <Popup.Subtitle>{description}</Popup.Subtitle>
      </Popup.Header>
      <Popup.Body>
        <VerificationCode
          errorMessage={otpErrorMessage}
          isDisabled={isPending}
          isInvalid={isOtpInvalid}
          onComplete={handleComplete}
          onSendSMS={onSendSMS}
        />
      </Popup.Body>
    </Popup.Root>
  );
}
